/**
 * Row-level validation of an uploaded concluintes file.
 *
 * The header check (`validarColunasConcluintes`) catches a column *named*
 * "CPF". This one catches the other half of the problem: a CPF sitting in a
 * column named "obs", a birth date typed into "turma", a phone number appended
 * to a student's name. A school does not set out to over-share — it exports
 * what its system happens to produce — so the file has to be read, not just its
 * header.
 *
 * ## Why this is pure and runs in the browser
 *
 * The panel validates the file **before uploading it**. A spreadsheet carrying
 * 300 minors' national ID numbers then never leaves the school's computer: it
 * is refused locally, and nothing about it reaches our infrastructure. The
 * server repeats the same check on the bytes it receives — a browser check is
 * convenience, never the control — but the order matters, because the best
 * outcome for that data is that we never held it.
 *
 * Pure module, no I/O, no clock. 100% coverage required (CLAUDE.md).
 */

import { CAMPOS_PROIBIDOS_CONCLUINTES } from '../constantes/index.js'
import { normalizarCabecalho } from './concluintes.js'

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export interface LinhaConcluinte {
  nomeCompleto: string
  curso: string
  anoConclusao: number
  turma: string | null
  ordem: number
}

export type GravidadeProblema = 'rejeita' | 'corrige' | 'avisa'

export interface ProblemaLinha {
  /** 1-based, counting the header as line 1 — the number the school sees. */
  linha: number
  coluna: string | null
  codigo: CodigoProblemaLinha
  mensagem: string
  gravidade: GravidadeProblema
}

export type CodigoProblemaLinha =
  | 'dado_proibido_no_valor'
  | 'nome_vazio'
  | 'nome_muito_curto'
  | 'nome_sem_sobrenome'
  | 'curso_vazio'
  | 'ano_invalido'
  | 'ano_fora_da_faixa'
  | 'duplicata'
  | 'arquivo_vazio'
  | 'excesso_de_linhas'

export interface ResultadoPlanilha {
  valido: boolean
  linhas: LinhaConcluinte[]
  problemas: ProblemaLinha[]
  /** Rows dropped because they were entirely blank. */
  linhasVazias: number
  /** Names that appear more than once, in the order first seen. */
  duplicadas: string[]
}

/**
 * Hard ceiling on rows.
 *
 * Not a performance guard: a "class list" with more than this is a school
 * exporting its entire student body by mistake, and the honest answer is to
 * stop and ask rather than to publish 20.000 minors' names in the official
 * gazette.
 */
export const MAXIMO_CONCLUINTES = 5_000

/** Oldest conclusion year worth accepting. Earlier is a typo, not history. */
const ANO_MINIMO = 1960

// ---------------------------------------------------------------------------
// Detecção de dado proibido dentro do valor
// ---------------------------------------------------------------------------

/**
 * Patterns for restricted data that arrives inside a cell.
 *
 * Each one is deliberately narrow. A rule that fires on "any 11 digits" would
 * reject a legitimate row for a school that writes the class code as a long
 * number, and a validator that cries wolf gets worked around — which is worse
 * than one that misses a case.
 */
const PADROES_NO_VALOR: Array<{ codigo: string; rotulo: string; regex: RegExp }> = [
  // CPF com máscara: 000.000.000-00
  { codigo: 'cpf', rotulo: 'CPF', regex: /\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/ },
  // CPF sem máscara só conta quando está sozinho na célula — solto no meio de
  // um texto, 11 dígitos podem ser telefone, matrícula ou código de turma.
  { codigo: 'cpf', rotulo: 'CPF', regex: /^\s*\d{11}\s*$/ },
  // Data completa: 12/03/2008 ou 2008-03-12
  {
    codigo: 'data_nascimento',
    rotulo: 'data de nascimento',
    regex: /\b(?:\d{2}[/-]\d{2}[/-]\d{4}|\d{4}-\d{2}-\d{2})\b/,
  },
  { codigo: 'email', rotulo: 'e-mail', regex: /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i },
  // Telefone brasileiro com DDD, com ou sem máscara.
  {
    codigo: 'telefone',
    rotulo: 'telefone',
    regex: /(?:\(\d{2}\)\s?|\b\d{2}\s)9?\d{4}[-\s]?\d{4}\b/,
  },
  // RG rotulado. Sem o rótulo não dá para distinguir de matrícula.
  { codigo: 'rg', rotulo: 'RG', regex: /\b(?:rg|registro\s+geral)\s*:?\s*[\d.]{5,}/i },
]

/** Restricted data found inside a cell value, if any. */
export function dadoProibidoNoValor(valor: string): { codigo: string; rotulo: string } | null {
  const texto = valor
  if (texto.trim() === '') return null

  for (const p of PADROES_NO_VALOR) {
    if (p.regex.test(texto)) return { codigo: p.codigo, rotulo: p.rotulo }
  }
  return null
}

// ---------------------------------------------------------------------------
// Normalização de valores
// ---------------------------------------------------------------------------

/** Collapses whitespace. Keeps accents and case — it is a person's name. */
function limpar(valor: unknown): string {
  return String(valor ?? '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Title-cases a name that arrived in ALL CAPS.
 *
 * School exports are frequently uppercase, and "MARIA DA SILVA" printed in the
 * gazette next to "Maria da Silva" looks like two different standards on the
 * same page. Names that are not fully uppercase are left exactly as typed —
 * guessing at someone's preferred capitalisation is not ours to do.
 */
export function normalizarNome(valor: string): string {
  const nome = limpar(valor)
  if (nome === '' || nome !== nome.toUpperCase()) return nome

  const minusculas = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'di', 'du', 'del', 'della'])
  return nome
    .toLocaleLowerCase('pt-BR')
    .split(' ')
    .map((palavra, i) => {
      if (i > 0 && minusculas.has(palavra)) return palavra
      // Preserva o hífen composto: "ana-maria" → "Ana-Maria".
      return palavra.replace(/(^|-)([\p{L}])/gu, (_, sep: string, letra: string) =>
        sep + letra.toLocaleUpperCase('pt-BR'),
      )
    })
    .join(' ')
}

/** Chave de comparação para detectar duplicata sem ser enganado por acento. */
function chaveNome(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

function paraAno(valor: unknown): number | null {
  const texto = limpar(valor)
  if (texto === '') return null

  // Aceita "2026", "2026.0" (planilha exporta número), "Dez/2026" e "2026/1".
  const encontrado = texto.match(/\b(19|20)\d{2}\b/)
  if (!encontrado) return null
  return Number(encontrado[0])
}

// ---------------------------------------------------------------------------
// Mapeamento de colunas
// ---------------------------------------------------------------------------

export interface MapaColunas {
  nome: number
  curso: number
  ano: number
  turma: number | null
}

/**
 * Locates the columns we need, by header.
 *
 * Returns null when a required column is absent — the caller has already run
 * `validarColunasConcluintes` and will have a better message than this could
 * produce.
 */
export function mapearColunas(cabecalhos: readonly string[]): MapaColunas | null {
  const normalizados = cabecalhos.map((c) => normalizarCabecalho(c).split('_'))

  const acharPor = (predicado: (tokens: string[]) => boolean): number =>
    normalizados.findIndex(predicado)

  const nome = acharPor((t) => t.includes('nome') || t.includes('aluno') || t.includes('concluinte'))
  const curso = acharPor((t) => t.includes('curso'))
  const ano = acharPor((t) => t.includes('ano') || t.includes('conclusao'))
  const turma = acharPor((t) => t.includes('turma'))

  if (nome < 0 || curso < 0 || ano < 0) return null
  return { nome, curso, ano, turma: turma < 0 ? null : turma }
}

// ---------------------------------------------------------------------------
// Validação
// ---------------------------------------------------------------------------

/**
 * Validates and normalises the data rows.
 *
 * `linhas` is the raw grid, header EXCLUDED. Every cell is scanned for
 * restricted data, including the ones we do not import — a CPF in a column we
 * ignore is still a CPF that was sent to us, and the school needs to know its
 * export is leaking.
 */
export function validarLinhasConcluintes(
  cabecalhos: readonly string[],
  linhas: readonly (readonly unknown[])[],
): ResultadoPlanilha {
  const problemas: ProblemaLinha[] = []
  const resultado: LinhaConcluinte[] = []
  const duplicadas: string[] = []
  const vistos = new Map<string, number>()
  let linhasVazias = 0

  const mapa = mapearColunas(cabecalhos)

  // Resolvidos uma vez: o índice vem de findIndex sobre este mesmo array, então
  // o cabeçalho existe. Repetir um fallback em cada mensagem seria código que
  // não pode rodar — e que ninguém consegue testar.
  const rotulos = {
    nome: (mapa && cabecalhos[mapa.nome]) || 'nome',
    curso: (mapa && cabecalhos[mapa.curso]) || 'curso',
    ano: (mapa && cabecalhos[mapa.ano]) || 'ano',
  }

  if (linhas.length === 0) {
    return {
      valido: false,
      linhas: [],
      problemas: [
        {
          linha: 1,
          coluna: null,
          codigo: 'arquivo_vazio',
          mensagem: 'O arquivo não tem nenhuma linha de dados.',
          gravidade: 'rejeita',
        },
      ],
      linhasVazias: 0,
      duplicadas: [],
    }
  }

  if (linhas.length > MAXIMO_CONCLUINTES) {
    problemas.push({
      linha: MAXIMO_CONCLUINTES + 2,
      coluna: null,
      codigo: 'excesso_de_linhas',
      mensagem:
        `O arquivo tem ${linhas.length} linhas, acima do limite de ${MAXIMO_CONCLUINTES}. ` +
        'Confira se não veio a base inteira de alunos no lugar da turma concluinte.',
      gravidade: 'rejeita',
    })
  }

  linhas.forEach((linha, i) => {
    // +2: a planilha conta o cabeçalho como linha 1, e o usuário conta a partir de 1.
    const numero = i + 2

    const vazia = linha.every((celula) => limpar(celula) === '')
    if (vazia) {
      linhasVazias++
      return
    }

    // Varre TODAS as células, inclusive as que não importamos.
    linha.forEach((celula, c) => {
      const achado = dadoProibidoNoValor(limpar(celula))
      if (achado) {
        problemas.push({
          linha: numero,
          coluna: cabecalhos[c] ?? `coluna ${c + 1}`,
          codigo: 'dado_proibido_no_valor',
          mensagem:
            `Encontramos o que parece ser ${achado.rotulo} no valor desta célula. ` +
            'A Resolução CEE nº 44/2020 publica apenas nome, curso, ano e turma — ' +
            'remova esse dado do arquivo antes de enviar.',
          gravidade: 'rejeita',
        })
      }
    })

    if (!mapa) return

    const nomeBruto = limpar(linha[mapa.nome])
    const curso = limpar(linha[mapa.curso])
    const ano = paraAno(linha[mapa.ano])
    const turma = mapa.turma === null ? null : limpar(linha[mapa.turma]) || null

    if (nomeBruto === '') {
      problemas.push({
        linha: numero,
        coluna: rotulos.nome,
        codigo: 'nome_vazio',
        mensagem: 'Linha sem nome do concluinte.',
        gravidade: 'rejeita',
      })
      return
    }

    const nome = normalizarNome(nomeBruto)

    if (nome.length < 3) {
      problemas.push({
        linha: numero,
        coluna: rotulos.nome,
        codigo: 'nome_muito_curto',
        mensagem: `"${nome}" é curto demais para ser um nome completo.`,
        gravidade: 'rejeita',
      })
      return
    }

    if (!nome.includes(' ')) {
      // Aviso, não rejeição: a Resolução pede a relação nominal e um nome sem
      // sobrenome quase sempre é erro de exportação — mas recusar a publicação
      // inteira por causa disso puniria a escola por um dado que ela pode
      // simplesmente corrigir depois de ver o aviso.
      problemas.push({
        linha: numero,
        coluna: rotulos.nome,
        codigo: 'nome_sem_sobrenome',
        mensagem: `"${nome}" está sem sobrenome. Confira antes de aprovar a prova.`,
        gravidade: 'avisa',
      })
    }

    if (curso === '') {
      problemas.push({
        linha: numero,
        coluna: rotulos.curso,
        codigo: 'curso_vazio',
        mensagem: 'Linha sem curso.',
        gravidade: 'rejeita',
      })
      return
    }

    if (ano === null) {
      problemas.push({
        linha: numero,
        coluna: rotulos.ano,
        codigo: 'ano_invalido',
        mensagem: `Não consegui ler um ano em "${limpar(linha[mapa.ano])}".`,
        gravidade: 'rejeita',
      })
      return
    }

    if (ano < ANO_MINIMO) {
      problemas.push({
        linha: numero,
        coluna: rotulos.ano,
        codigo: 'ano_fora_da_faixa',
        mensagem: `Ano de conclusão ${ano} parece errado.`,
        gravidade: 'rejeita',
      })
      return
    }

    const chave = `${chaveNome(nome)}|${chaveNome(curso)}|${ano}`
    const anterior = vistos.get(chave)
    if (anterior !== undefined) {
      // Duplicata é aviso: turmas homônimas existem, e o mesmo nome pode
      // aparecer duas vezes legitimamente. Publicar o nome duplicado, porém, é
      // um erro visível na página — então a escola precisa ver isso antes.
      if (!duplicadas.includes(nome)) duplicadas.push(nome)
      problemas.push({
        linha: numero,
        coluna: rotulos.nome,
        codigo: 'duplicata',
        mensagem: `"${nome}" já aparece na linha ${anterior}, no mesmo curso e ano.`,
        gravidade: 'avisa',
      })
    } else {
      vistos.set(chave, numero)
    }

    resultado.push({
      nomeCompleto: nome,
      curso,
      anoConclusao: ano,
      turma,
      ordem: resultado.length,
    })
  })

  const valido = problemas.every((p) => p.gravidade !== 'rejeita') && resultado.length > 0

  if (resultado.length === 0 && problemas.every((p) => p.codigo !== 'arquivo_vazio')) {
    problemas.push({
      linha: 1,
      coluna: null,
      codigo: 'arquivo_vazio',
      mensagem: 'Nenhuma linha aproveitável foi encontrada no arquivo.',
      gravidade: 'rejeita',
    })
  }

  return { valido, linhas: resultado, problemas, linhasVazias, duplicadas }
}

/**
 * One-line summary for the school.
 *
 * Leads with the blocking count, because that is the only number that decides
 * whether the file can be sent.
 */
export function resumirProblemas(r: ResultadoPlanilha): string {
  const rejeicoes = r.problemas.filter((p) => p.gravidade === 'rejeita').length
  const avisos = r.problemas.filter((p) => p.gravidade === 'avisa').length

  if (rejeicoes > 0) {
    return (
      `${rejeicoes} problema${rejeicoes === 1 ? '' : 's'} impede${rejeicoes === 1 ? '' : 'm'} o envio` +
      (avisos > 0 ? ` · ${avisos} aviso${avisos === 1 ? '' : 's'}` : '')
    )
  }
  if (avisos > 0) {
    return `${r.linhas.length} concluintes · ${avisos} aviso${avisos === 1 ? '' : 's'} para conferir`
  }
  return `${r.linhas.length} concluintes, tudo certo`
}

/** Sanity note: forbidden-field list is shared with the header validator. */
export const TERMOS_PROIBIDOS = CAMPOS_PROIBIDOS_CONCLUINTES
